/**
 * @fileoverview ヒヤリハット 詳細・対応ドロワー
 * @description 人材/船舶の詳細ドロワーに合わせた右スライド型。状況・現場写真の確認と、
 *              原因/対策/重要度/ステータスのレビュー対応をこの1画面で完結する。
 */
import React, { useState, useEffect } from 'react';
import {
  X, MapPin, User, Ship, Calendar, Tag, Clock, AlertTriangle,
  Camera, Trash2, ClipboardList,
} from 'lucide-react';
import { getToken } from '../services/api';
import * as api from '../services/api';

export interface Incident {
  id: string; reporter_name: string | null; vessel_name: string | null; vessel_id: string | null;
  occurred_at: string; location: string | null; category: string | null; severity: string;
  title: string; description: string; cause: string | null; countermeasure: string | null;
  status: string; has_image: boolean; created_at: string | null;
}

export const SEVERITY_META: Record<string, { label: string; bg: string; fg: string; border: string }> = {
  critical: { label: '重大', bg: '#fef2f2', fg: '#dc2626', border: '#fecaca' },
  warning:  { label: '注意', bg: '#fffbeb', fg: '#d97706', border: '#fde68a' },
};
export const STATUS_META: Record<string, { label: string; bg: string; fg: string; border: string }> = {
  open:      { label: '未対応', bg: '#fef2f2', fg: '#dc2626', border: '#fecaca' },
  reviewing: { label: '対応中', bg: '#fffbeb', fg: '#d97706', border: '#fde68a' },
  closed:    { label: '完了',   bg: '#f0fdf4', fg: '#16a34a', border: '#bbf7d0' },
};

export function Pill({ meta }: { meta?: { label: string; bg: string; fg: string; border: string } }) {
  const m = meta ?? { label: '-', bg: '#f1f5f9', fg: '#64748b', border: '#e2e8f0' };
  return (
    <span style={{ padding: '3px 11px', borderRadius: 999, fontSize: 11.5, fontWeight: 700, background: m.bg, color: m.fg, border: `1px solid ${m.border}`, whiteSpace: 'nowrap' }}>
      {m.label}
    </span>
  );
}

export const fmtDateTime = (v?: string | null) => (v ? new Date(v).toLocaleString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }) : '-');
export const daysSince = (v?: string | null): number | null => {
  if (!v) return null;
  const d = new Date(v); if (isNaN(d.getTime())) return null;
  return Math.max(0, Math.floor((Date.now() - d.getTime()) / 86400000));
};

// ── 認証付き画像 ─────────────────────────────────────────────────────────────
function IncidentImage({ id }: { id: string }) {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    const subdomain = (() => { const h = window.location.hostname; if (h !== 'localhost' && h !== '127.0.0.1') { const p = h.split('.'); if (p.length >= 3 && p[0]) return p[0]; } return (import.meta.env.VITE_COMPANY_SUBDOMAIN as string | undefined) ?? 'unison-tech'; })();
    const apiBase = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? 'http://localhost:3001/api';
    const token = getToken() ?? '';
    let objectUrl: string | null = null;
    fetch(`${apiBase}/incidents/${id}/image`, { headers: { Authorization: `Bearer ${token}`, 'X-Company-Subdomain': subdomain, 'X-App-Type': 'admin' } })
      .then(r => (r.ok ? r.blob() : Promise.reject()))
      .then(b => { objectUrl = URL.createObjectURL(b); setSrc(objectUrl); })
      .catch(() => {});
    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [id]);
  if (!src) return null;
  return <img src={src} alt="現場写真" style={{ width: '100%', borderRadius: 10, border: '1px solid #e2e8f0' }} />;
}

const InfoItem: React.FC<{ icon: React.ReactNode; label: string; children: React.ReactNode }> = ({ icon, label, children }) => (
  <div>
    <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.04em', marginBottom: 3, display: 'flex', alignItems: 'center', gap: 4 }}>{icon}{label}</div>
    <div style={{ fontSize: 13.5, color: '#1e293b', fontWeight: 500, wordBreak: 'break-word' }}>{children}</div>
  </div>
);

const SectionTitle: React.FC<{ icon: React.ReactNode; title: string }> = ({ icon, title }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '20px 0 10px' }}>
    <span style={{ color: '#0369a1', display: 'flex' }}>{icon}</span>
    <span style={{ fontSize: 13.5, fontWeight: 800, color: '#0f172a' }}>{title}</span>
  </div>
);

// ── 本体 ────────────────────────────────────────────────────────────────────
const IncidentDrawer: React.FC<{
  incident: Incident;
  categoryColor?: string;
  onClose: () => void;
  onChanged: () => void;
  onDeleted: () => void;
}> = ({ incident, categoryColor, onClose, onChanged, onDeleted }) => {
  const [cause, setCause] = useState(incident.cause ?? '');
  const [countermeasure, setCountermeasure] = useState(incident.countermeasure ?? '');
  const [severity, setSeverity] = useState(incident.severity ?? 'warning');
  const [status, setStatus] = useState(incident.status ?? 'open');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setCause(incident.cause ?? ''); setCountermeasure(incident.countermeasure ?? '');
    setSeverity(incident.severity ?? 'warning'); setStatus(incident.status ?? 'open'); setSaved(false);
  }, [incident.id]);

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  const dirty = cause !== (incident.cause ?? '') || countermeasure !== (incident.countermeasure ?? '')
    || severity !== incident.severity || status !== incident.status;

  const save = async () => {
    setSaving(true);
    try {
      await api.patch('/incidents/' + incident.id, { cause, countermeasure, severity, status });
      setSaved(true);
      onChanged();
      setTimeout(() => setSaved(false), 2000);
    } catch (e: any) { alert(e.message ?? '保存に失敗しました'); }
    setSaving(false);
  };
  const remove = async () => {
    if (!window.confirm('この報告を削除しますか？この操作は取り消せません。')) return;
    try { await api.del('/incidents/' + incident.id); onDeleted(); }
    catch (e: any) { alert(e.message ?? '削除に失敗しました'); }
  };

  const openDays = ['open', 'reviewing'].includes(status) ? daysSince(incident.occurred_at) : null;

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.35)', zIndex: 900 }} />
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(620px, 95vw)', zIndex: 901,
        background: '#fff', boxShadow: '-12px 0 40px rgba(15,23,42,0.18)',
        display: 'flex', flexDirection: 'column', animation: 'incDrawerIn .22s ease',
      }}>
        <style>{`@keyframes incDrawerIn { from { transform: translateX(40px); opacity: 0; } to { transform: none; opacity: 1; } }`}</style>

        {/* ヘッダー */}
        <div style={{ padding: '18px 24px 16px', borderBottom: '1px solid #e2e8f0', background: 'linear-gradient(135deg, #f8fafc 0%, #fff5f5 100%)', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                <Pill meta={SEVERITY_META[severity]} />
                <Pill meta={STATUS_META[status]} />
                {incident.category && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 10px', borderRadius: 999, fontSize: 11.5, fontWeight: 700, background: (categoryColor ?? '#64748b') + '18', color: categoryColor ?? '#64748b', border: `1px solid ${(categoryColor ?? '#64748b')}40` }}>
                    <Tag size={11} />{incident.category}
                  </span>
                )}
                {openDays != null && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 10px', borderRadius: 999, fontSize: 11.5, fontWeight: 700, background: openDays >= 14 ? '#fef2f2' : '#f1f5f9', color: openDays >= 14 ? '#dc2626' : '#64748b', border: `1px solid ${openDays >= 14 ? '#fecaca' : '#e2e8f0'}` }}>
                    <Clock size={11} />未対応 {openDays}日
                  </span>
                )}
              </div>
              <div style={{ fontSize: 19, fontWeight: 800, color: '#0f172a', lineHeight: 1.35 }}>{incident.title}</div>
            </div>
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 6 }}><X size={20} /></button>
          </div>
        </div>

        {/* ボディ */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '4px 24px 28px' }}>
          <SectionTitle icon={<ClipboardList size={15} />} title="報告内容" />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 16px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16 }}>
            <InfoItem icon={<Calendar size={12} />} label="発生日時">{fmtDateTime(incident.occurred_at)}</InfoItem>
            <InfoItem icon={<MapPin size={12} />} label="場所">{incident.location || '-'}</InfoItem>
            <InfoItem icon={<User size={12} />} label="報告者">{incident.reporter_name || '-'}</InfoItem>
            <InfoItem icon={<Ship size={12} />} label="船舶">{incident.vessel_name || '-'}</InfoItem>
          </div>

          <div style={{ marginTop: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.04em', marginBottom: 6 }}>状況</div>
            <div style={{ fontSize: 13.5, color: '#0f172a', whiteSpace: 'pre-wrap', lineHeight: 1.75, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '12px 14px' }}>{incident.description}</div>
          </div>

          {incident.has_image && (
            <div style={{ marginTop: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.04em', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 4 }}><Camera size={12} />現場写真</div>
              <IncidentImage id={incident.id} />
            </div>
          )}

          {/* レビュー対応 */}
          <SectionTitle icon={<AlertTriangle size={15} />} title="レビュー対応" />
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16 }}>
            <div style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
              <div style={{ flex: 1 }}>
                <label className="form-l">重要度</label>
                <select className="form-i" value={severity} onChange={e => setSeverity(e.target.value)}>
                  <option value="warning">注意</option>
                  <option value="critical">重大</option>
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <label className="form-l">ステータス</label>
                <select className="form-i" value={status} onChange={e => setStatus(e.target.value)}>
                  <option value="open">未対応</option>
                  <option value="reviewing">対応中</option>
                  <option value="closed">完了</option>
                </select>
              </div>
            </div>
            <div className="form-g" style={{ marginBottom: 12 }}>
              <label className="form-l">原因</label>
              <textarea className="form-i" rows={3} value={cause} placeholder="なぜ発生したか（要因分析）" onChange={e => setCause(e.target.value)} style={{ resize: 'vertical' }} />
            </div>
            <div className="form-g">
              <label className="form-l">再発防止策</label>
              <textarea className="form-i" rows={3} value={countermeasure} placeholder="今後の対策・水平展開" onChange={e => setCountermeasure(e.target.value)} style={{ resize: 'vertical' }} />
            </div>
          </div>
        </div>

        {/* フッター */}
        <div style={{ flexShrink: 0, borderTop: '1px solid #e2e8f0', padding: '12px 24px', display: 'flex', alignItems: 'center', gap: 10, background: '#fff' }}>
          <button onClick={remove} title="削除"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '9px 14px', background: '#fff', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
            <Trash2 size={14} />削除
          </button>
          {saved && <span style={{ fontSize: 13, color: '#059669', fontWeight: 700 }}>✓ 保存しました</span>}
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <button onClick={onClose} className="btn-o">閉じる</button>
            <button onClick={save} disabled={saving || !dirty}
              style={{ padding: '9px 22px', border: 'none', borderRadius: 8, background: (saving || !dirty) ? '#93c5fd' : '#1d4ed8', color: '#fff', fontWeight: 700, cursor: (saving || !dirty) ? 'not-allowed' : 'pointer', fontSize: 13 }}>
              {saving ? '保存中…' : '保存'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default IncidentDrawer;
